/**
 * Hook unique pour charger TOUTES les données du dashboard
 * Évite de charger crews/prospects 3 fois séparément
 */

import { useState, useEffect, useMemo } from 'react';
import { useSupabase } from './useSupabase';
import { useUsers } from '@/contexts/UsersContext';
import { CREW_STATUS, type Crew } from '@/types/crew';
import type { FormSubmission } from '@/types/form';
import type { Task } from '@/types/task';
import { monitorQuery } from '@/lib/supabase-monitor';
import { fetchAllPages } from '@/lib/supabase-paginate';
import { getStartDateForPeriod, type PeriodValue } from '@/components/ui';

export interface CallsByPeriodData {
  label: string;
  [key: string]: string | number;
}

/**
 * Ligne minimale de `contacts` chargée pour le graphique « Contacts par statut ».
 * `status` est typé `string` (et non `ContactStatus`) car l'enum DB `contact_status`
 * contient des valeurs (`registered`, `client`) absentes du type TS : on compare à
 * des littéraux connus, le typage strict casserait sans bénéfice.
 */
interface ContactStatusRow {
  id: string;
  status: string;
}

// Alias pour rétrocompatibilité
export type CallsByMonthData = CallsByPeriodData;

interface DashboardDataOptions {
  /** Période pour le graphique des appels */
  callsPeriod?: PeriodValue;
  /** Ne pas charger les stats d'appels (pour les users) */
  skipCallStats?: boolean;
}

export function useDashboardData(options: DashboardDataOptions = {}) {
  const { callsPeriod = '6m', skipCallStats = false } = options;
  const supabase = useSupabase();
  const { usersMap } = useUsers();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [contacts, setContacts] = useState<ContactStatusRow[]>([]);
  const [prospects, setProspects] = useState<FormSubmission[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [callsByPeriod, setCallsByPeriod] = useState<CallsByPeriodData[]>([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [loading, setLoading] = useState(true);

  // ✅ Charger toutes les données en parallèle
  useEffect(() => {
    async function fetchAllData() {
      setLoading(true);
      try {
        // R11 : aligner les compteurs TdB avec les listes filtrées en
        // contournant le cap PostgREST 1000 (`fetchAllPages`). Sans pagination,
        // les queries ci-dessous étaient tronquées au-delà de 1000 lignes.
        // CRM-12 : exclure les archivés dès la requête (doublons, expirés)
        const buildCrewsQuery = () =>
          supabase
            .from('crews')
            .select(
              `
              *,
              product:products(id, name),
              members:crew_members(contact:contacts(id))
            `
            )
            .neq('status', CREW_STATUS.ARCHIVED)
            .order('created_at', { ascending: false });

        // r-dash-align : le graphique « Contacts par statut » doit compter les
        // CONTACTS par `contacts.status` (cohérent avec son intitulé et la liste
        // des contacts que le client regarde), pas les équipages. On charge donc
        // les contacts via `fetchAllPages` (aucun cap) — id + status suffisent.
        const buildContactsQuery = () =>
          supabase.from('contacts').select('id, status').order('id', { ascending: true });

        // r-dash-align : aligner le compteur « Prospect » du dashboard sur le
        // TOTAL exact de la liste prospects = MÊME source/filtre que la page
        // Prospects (`prospects_view` + exclusion `payment_status = 'paid'`,
        // cf. useProspects.fetchPaginatedFromView). La vue filtre déjà
        // `converted_at IS NULL`. Sans cela le dashboard pouvait inclure des
        // soumissions payées que la liste exclut → écart.
        const buildProspectsQuery = () =>
          supabase
            .from('prospects_view')
            .select('id, payment_status')
            .or('payment_status.neq.paid,payment_status.is.null')
            .order('id', { ascending: true });

        const buildTasksQuery = () =>
          supabase.from('tasks').select('*').order('due_date', { ascending: true });

        // Charger en parallèle pour être plus rapide
        const [crewsResult, contactsResult, prospectsResult, tasksResult] = await Promise.all([
          monitorQuery(
            'crews',
            'select',
            'all crews for dashboard (paginated)',
            (() => fetchAllPages(buildCrewsQuery)) as any,
            'useDashboardData.fetchCrews'
          ),

          monitorQuery(
            'contacts',
            'select',
            'all contacts for dashboard status chart (paginated)',
            (() => fetchAllPages(buildContactsQuery)) as any,
            'useDashboardData.fetchContacts'
          ),

          // Item #13 + r-dash-align : aligner avec la page Prospects
          // (`prospects_view`, exclut converties ET payées).
          monitorQuery(
            'prospects_view',
            'select',
            'all prospects for dashboard (paginated)',
            (() => fetchAllPages(buildProspectsQuery)) as any,
            'useDashboardData.fetchProspects'
          ),

          monitorQuery(
            'tasks',
            'select',
            'all tasks for dashboard (paginated)',
            (() => fetchAllPages(buildTasksQuery)) as any,
            'useDashboardData.fetchTasks'
          ),
        ]);

        if (!crewsResult.error && crewsResult.data) {
          setCrews(crewsResult.data as Crew[]);
        }

        if (!contactsResult.error && contactsResult.data) {
          setContacts(contactsResult.data as ContactStatusRow[]);
        }

        if (!prospectsResult.error && prospectsResult.data) {
          setProspects(prospectsResult.data as FormSubmission[]);
        }

        if (!tasksResult.error && tasksResult.data) {
          setTasks(tasksResult.data as Task[]);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, [supabase, usersMap]);

  // ✅ Charger les appels séparément (pour pouvoir changer la période sans tout recharger)
  useEffect(() => {
    async function fetchCallsData() {
      // Ne pas charger si skipCallStats est activé (pour les users)
      if (skipCallStats) return;
      if (Object.keys(usersMap).length === 0) return;

      setLoadingCalls(true);
      try {
        const now = new Date();
        const startDate = getStartDateForPeriod(callsPeriod);

        // Construire la requête
        // Item #32 : ne compter QUE les appels répondus dans la stat dashboard.
        // Les non-répondus / répondeurs restent visibles ailleurs (cards séparées),
        // mais la courbe principale qui sert à programmer les relances ne doit
        // refléter que les contacts effectivement joints.
        let query = supabase
          .from('timeline_events')
          .select('created_at, created_by, metadata')
          .eq('event_type', 'call_logged')
          .eq('metadata->>outcome', 'answered');

        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }

        const callsResult = await monitorQuery(
          'timeline_events',
          'select',
          `calls for period ${callsPeriod}`,
          (() => query) as any,
          'useDashboardData.fetchCalls'
        );

        if (!callsResult.error && callsResult.data) {
          const calls = callsResult.data;

          // Déterminer le mode de groupement selon la période
          // 7d, 30d : par jour
          // 90d : par semaine
          // 6m, 12m, all : par mois
          const groupBy =
            callsPeriod === '7d' || callsPeriod === '30d'
              ? 'day'
              : callsPeriod === '90d'
                ? 'week'
                : 'month';

          const periodsMap: Record<string, any> = {};
          const monthNames = [
            'Jan',
            'Fév',
            'Mar',
            'Avr',
            'Mai',
            'Juin',
            'Juil',
            'Août',
            'Sep',
            'Oct',
            'Nov',
            'Déc',
          ];
          const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

          // Fonction pour obtenir la clé et le label d'une période
          const getPeriodInfo = (date: Date) => {
            if (groupBy === 'day') {
              const key = date.toISOString().split('T')[0];
              const label = `${dayNames[date.getDay()]} ${date.getDate()}`;
              return { key, label };
            } else if (groupBy === 'week') {
              // Semaine ISO
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);
              d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
              const week1 = new Date(d.getFullYear(), 0, 4);
              const weekNum =
                1 +
                Math.round(
                  ((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
                );
              const key = `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
              const label = `S${weekNum}`;
              return { key, label };
            } else {
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              const label = monthNames[date.getMonth()];
              return { key, label };
            }
          };

          // Initialiser les périodes
          const initPeriods = () => {
            const periods: { key: string; label: string }[] = [];

            if (groupBy === 'day') {
              const daysCount = callsPeriod === '7d' ? 7 : 30;
              for (let i = daysCount - 1; i >= 0; i--) {
                const date = new Date(now);
                date.setDate(now.getDate() - i);
                periods.push(getPeriodInfo(date));
              }
            } else if (groupBy === 'week') {
              const weeksCount = 13; // ~90 jours
              for (let i = weeksCount - 1; i >= 0; i--) {
                const date = new Date(now);
                date.setDate(now.getDate() - i * 7);
                const info = getPeriodInfo(date);
                if (!periods.find((p) => p.key === info.key)) {
                  periods.push(info);
                }
              }
            } else {
              const monthsCount = callsPeriod === '6m' ? 6 : callsPeriod === '12m' ? 12 : 12;
              for (let i = monthsCount - 1; i >= 0; i--) {
                const date = new Date(now);
                date.setMonth(now.getMonth() - i);
                periods.push(getPeriodInfo(date));
              }
            }

            return periods;
          };

          const periods = initPeriods();
          periods.forEach(({ key, label }) => {
            periodsMap[key] = { label };
            Object.values(usersMap).forEach((user) => {
              const userName = user.full_name || user.email;
              periodsMap[key][userName] = 0;
            });
          });

          // Compter les appels par période et par utilisateur
          (calls as any[]).forEach((call: any) => {
            const date = new Date(call.created_at);
            const { key } = getPeriodInfo(date);

            if (periodsMap[key] && call.created_by && usersMap[call.created_by]) {
              const userName =
                usersMap[call.created_by].full_name || usersMap[call.created_by].email;
              if (periodsMap[key][userName] !== undefined) {
                periodsMap[key][userName] += 1;
              }
            }
          });

          const chartData = Object.keys(periodsMap)
            .sort()
            .map((key) => periodsMap[key]);

          setCallsByPeriod(chartData);
        }
      } finally {
        setLoadingCalls(false);
      }
    }

    fetchCallsData();
  }, [supabase, usersMap, callsPeriod, skipCallStats]);

  // Statistiques globales
  const stats = useMemo(() => {
    // Retour réunion 07/07 (B) : le compteur « Prospect » du dashboard doit
    // refléter le STATUT `contacts.status = 'prospect'` (nb de contacts au statut
    // prospect, renommé « demandes entrantes » côté client), PAS l'origine
    // (vitrine + préinscription via `prospects_view`). Les contacts sont déjà
    // chargés intégralement (fetchAllPages, aucun cap) → comptage local.
    const totalProspects = contacts.filter((c) => c.status === 'prospect').length;
    const totalCrews = crews.length;
    // Équipages avec BED complet ou terminés (statuts "validés")
    const bedCompleteCrews = crews.filter(
      (c) => c.status === 'bed_complete' || c.status === 'completed'
    ).length;
    const expectedRevenue = crews.reduce((sum, crew) => sum + (crew.budget || 0), 0);

    const pendingTasks = tasks.filter((task) => !task.completed);
    const now = new Date();
    const threeDaysLater = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const urgentTasks = pendingTasks.filter((task) => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return dueDate >= now && dueDate <= threeDaysLater;
    });

    // Taux de conversion : équipages BED complet ou terminés / total équipages
    const conversionRate = totalCrews > 0 ? Math.round((bedCompleteCrews / totalCrews) * 100) : 0;

    return {
      totalProspects,
      totalCrews,
      bedCompleteCrews,
      conversionRate,
      expectedRevenue,
      totalPendingTasks: pendingTasks.length,
      urgentTasksCount: urgentTasks.length,
      urgentTasks,
    };
  }, [contacts, crews, tasks]);

  // Données pour graphique "Contacts par statut"
  // r-dash-align : compté par `contacts.status` (PAS `crews.status`) pour être
  // cohérent avec l'intitulé du graphique et avec la liste des contacts filtrée
  // par statut → zéro écart dashboard ↔ liste contacts.
  // Retour réunion 07/07 (B) : le bar « Prospect » compte désormais
  // `contacts.status = 'prospect'` (cohérent avec le KPI global ci-dessus et la
  // liste contacts filtrée par statut), et non plus le total origine
  // `prospects_view`.
  // La ligne « BPI en attente » (statut purement équipage `bpi_payment_pending`,
  // sans équivalent `contacts.status` ni onglet dans la liste contacts) est
  // retirée : elle n'a pas sa place dans un bloc « Contacts par statut ».
  const contactsByStatus = useMemo(() => {
    const countContacts = (...statuses: string[]) =>
      contacts.filter((c) => statuses.includes(c.status)).length;

    return [
      { status: 'Prospect', count: countContacts('prospect') },
      // 00232 : les deux nouveaux statuts BED sont repliés sur les cases
      // existantes (bed_downloaded = pré-inscrit qui a ouvert son bulletin,
      // bed_incomplete_payment = BED incomplet), pour que les totaux du
      // dashboard restent comparables d'une édition à l'autre.
      { status: 'Pré-inscrit', count: countContacts('pre_registered', 'bed_downloaded') },
      {
        status: 'BED incomplet',
        count: countContacts('bed_incomplete', 'bed_incomplete_payment'),
      },
      { status: 'BED complet', count: countContacts('bed_complete') },
      {
        status: 'Annulation',
        count: countContacts('cancellation_compliant', 'cancellation_non_compliant'),
      },
      {
        status: 'Report',
        count: countContacts('postponement_compliant', 'postponement_non_compliant'),
      },
      { status: 'Inscrit définitif', count: countContacts('completed') },
    ];
  }, [contacts]);

  // Alias pour rétrocompatibilité
  const callsByMonth = callsByPeriod;

  return {
    crews,
    prospects,
    tasks,
    stats,
    contactsByStatus,
    callsByMonth,
    callsByPeriod,
    loading,
    loadingCalls,
  };
}
